//Copyright - Kyley Harris (2021) - All rights to copy and reuse allowed
import { write } from '../language/lang';
import * as db from './indexed-db';
import { GetOptions } from './indexed-db';

// wrap indexed db in a handler.
function information(title: string, message: string) {
  alert(title + ' ' + message);
}

export abstract class IndexedDBAccess {
  protected _connection: IDBDatabase | null;

  constructor() {
    this._connection = null;
  }

  // Subclasses MUST supply the DB name. Made abstract (was a runtime
  // `throw new Error('override dbname')` stub) so TS catches a missing
  // override at compile time rather than the first call.
  public abstract dbName(): string;

  public async connect(): Promise<IDBDatabase> {
    if (this._connection) {
      db.log('open connection requested again');
      return this._connection;
    }

    this._connection = await db.openDb({
      dbName: this.dbName(),
      version: this.version(),
      upgrades: this.getDatabaseUpgrades(),
      onBlocked: () => {
        information(
          write`Database Access Blocked`,
          write`Please close all other browser tabs/windows accessing this page to continue`
        );
      },
    });

    this._connection.onversionchange = () => {
      if (this._connection) this._connection.close();
      db.log('a new version of the databse is ready, please reload this page');
      information(
        write`Application Update`,
        write`Please reload the page to continue`
      );
    };

    return this._connection;
  }

  protected async getFirstMatchingRecord<T>(
    objectStore: string,
    options?: GetOptions
  ): Promise<T | null> {
    const conn = await this.connect();
    const log = await db.get<T>(conn, objectStore, options);
    this.close();
    return log;
  }

  store(type: { name: string }): string {
    return type.name;
  }

  protected async getAll<T>(
    objectStore: string,
    options?: GetOptions
  ): Promise<T[]> {
    const conn = await this.connect();
    try {
      return db.getAll<T>(conn, objectStore, options);
    } finally {
      this.close();
    }
  }

  protected async put<T>(item: T, objectStore: string): Promise<boolean> {
    const conn = await this.connect();
    try {
      await db.put<T>(conn, objectStore, item);
      return true;
    } catch {
      return false;
    } finally {
      this.close();
    }
  }

  // Primary-key fast path. Uses store.get(id) (one index probe) instead
  // of the cursor-based getFirstMatchingRecord. Returns null when the
  // key isn't present — distinct from getFirstMatchingRecord which
  // wraps that case identically but has a heavier path.
  protected async getById<T>(
    id: IDBValidKey,
    objectStore: string
  ): Promise<T | null> {
    const conn = await this.connect();
    try {
      return await db.getById<T>(conn, objectStore, id);
    } finally {
      this.close();
    }
  }

  protected async putMany<T>(
    items: T[],
    objectStore: string
  ): Promise<boolean> {
    const conn = await this.connect();
    try {
      await db.putAll<T>(conn, objectStore, items);
      return true;
    } catch {
      return false;
    } finally {
      this.close();
    }
  }

  // Single-transaction batch insert / replace. Exposes the low-level
  // putAll on the wrapper API so consumers don't have to drop to raw
  // db.* primitives. Use when inserting > 1 row in one operation;
  // saves N-1 transaction commits.
  protected async bulkPut<T>(
    items: T[],
    objectStore: string
  ): Promise<boolean> {
    if (items.length === 0) return true;
    return this.putMany(items, objectStore);
  }

  protected async del(id: IDBValidKey, objectStore: string): Promise<void> {
    const conn = await this.connect();
    try {
      await db.del(conn, objectStore, id);
    } finally {
      this.close();
    }
  }

  protected version(): number {
    let version = 1;
    this.getDatabaseUpgrades().forEach(
      (x) => (version = Math.max(version, x.version))
    );
    return version;
  }

  // Subclasses MUST supply an upgrade list (one entry per schema
  // version). Abstract for compile-time enforcement; the prior stub
  // threw at runtime on the first connect().
  protected abstract getDatabaseUpgrades(): db.DatabaseUpgrade[];

  protected close() {
    if (this._connection) {
      this._connection.close();
      this._connection = null;
    }
  }
}
